import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LifeBuoy, MessageSquare, Send, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = [
  { value: "booking",       label: "Booking or scheduling" },
  { value: "payment",       label: "Payment or invoice"    },
  { value: "cleaner",       label: "Cleaner performance"   },
  { value: "verification",  label: "Verification / ID"     },
  { value: "payouts",       label: "Payouts / earnings"    },
  { value: "bug",           label: "Bug or technical issue" },
  { value: "account",       label: "Account / sign-in"     },
  { value: "other",         label: "Something else"        },
];

export default function SupportCenter() {
  const [faq, setFaq] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({ subject: "", category: "other", message: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [f, t] = await Promise.all([
        api.get("/support/faq"),
        api.get("/support/tickets"),
      ]);
      setFaq(f.data.items || []);
      setTickets(t.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject.trim() || form.message.trim().length < 10) {
      return toast.error("Please add a subject and describe the issue (10+ characters)");
    }
    setSubmitting(true);
    try {
      await api.post("/support/tickets", form);
      toast.success("Support ticket sent — we'll be in touch shortly");
      setForm({ subject: "", category: "other", message: "", email: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSubmitting(false); }
  };

  const grouped = faq.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="support-center">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Help &amp; Support</div>
        <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">How can CLEANBAZ help?</h1>
        <p className="text-slate-600 mt-2">Answers to common questions, plus a direct line to our team.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        {/* FAQ */}
        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <LifeBuoy className="w-5 h-5 text-teal-600" />Frequently asked questions
            </h2>
            {Object.keys(grouped).length === 0 ? (
              <div className="text-slate-500 text-sm py-6">Loading…</div>
            ) : Object.entries(grouped).map(([cat, arr]) => (
              <div key={cat} className="mb-5" data-testid={`faq-cat-${cat.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{cat}</div>
                <div className="space-y-1">
                  {arr.map((f, idx) => {
                    const k = `${cat}-${idx}`;
                    const open = !!expanded[k];
                    return (
                      <button
                        key={k}
                        onClick={() => setExpanded(e => ({ ...e, [k]: !e[k] }))}
                        data-testid={`faq-q-${k.toLowerCase().replace(/\s+/g,'-')}`}
                        className="w-full text-left px-3 py-3 rounded-lg border border-slate-200 hover:border-teal-300 bg-white transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          {open ? <ChevronDown className="w-4 h-4 text-teal-600 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-slate-400 mt-0.5" />}
                          <div className="flex-1">
                            <div className="text-sm font-medium text-slate-800">{f.q}</div>
                            {open && <div className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{f.a}</div>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Contact form + past tickets */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-6 space-y-3">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-teal-600" />Contact CLEANBAZ support
              </h2>
              <div><Label>Subject *</Label><Input data-testid="sup-subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Category</Label>
                <select data-testid="sup-cat" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="mt-1 w-full h-10 border rounded-md px-3 text-sm">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div><Label>Message *</Label><Textarea rows={5} data-testid="sup-msg" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="mt-1" placeholder="Give us as much detail as you can. Booking IDs, screenshots, and timestamps help." /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Reply email</Label><Input data-testid="sup-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="Defaults to account" /></div>
                <div><Label>Phone</Label><Input data-testid="sup-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="Optional" /></div>
              </div>
              <Button onClick={submit} disabled={submitting} data-testid="sup-submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                <Send className="w-4 h-4 mr-1" />{submitting ? "Sending…" : "Send message"}
              </Button>
            </CardContent>
          </Card>

          {tickets.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Your recent tickets</h3>
                <div className="space-y-2" data-testid="sup-tickets">
                  {tickets.map(t => (
                    <div key={t.id} className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={`sup-ticket-${t.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-slate-800 text-sm truncate">{t.subject}</div>
                        <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">{t.status}</Badge>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">{t.category} · {new Date(t.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
